"use client"

import { useState } from "react"
import Image from "next/image"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { useCountry } from "@/components/layout/country-provider"
import {
  ProductInfoButton,
  type BiomarkerGroup,
} from "@/components/ui/product-info-modal"
import { PersonPackageIcon } from "@/components/ui/package-icons"
import { blobMediaUrl } from "@/lib/blob-url"
import { formatNumber } from "@/lib/format"
import type { Recommendation, Answers, AddOnId } from "@/lib/quiz/types"
import type { PriceMap } from "@/lib/pricing"
import { addOnPriceKey, totalPrice } from "@/lib/quiz/product-mapping"
import {
  baseTierInfo,
  baseTierBookingProductId,
  addOnInfo,
} from "@/lib/quiz/product-info-map"
import { resolveAddOnIds } from "@/lib/quiz/resolve-add-on-ids"
import { bookingUrlForCountry } from "@/lib/booking"
import { withQuizAttribution } from "@/lib/quiz/booking-source"
import { track } from "@/lib/analytics"
import { resolveCoverageState } from "@/lib/quiz/coverage"
import type { InsurersData } from "@/lib/quiz/insurers"
import type { ServiceId, BaseTierId } from "@/lib/quiz/types"
import { memberPriceChf } from "@/lib/pricing/member-prices"
import { membershipTotalChf } from "@/lib/quiz/membership-pricing"
import { isQuizMembershipEligible } from "@/lib/quiz/membership-variant"
import { isBookable, type QuizMarket } from "@/lib/quiz/market"
import { useWaitlist } from "@/components/ui/waitlist-context"
import {
  RecommendationCard,
  type RecommendationAddOn,
} from "./recommendation-card"
import { QuizMembershipSideBySide } from "./membership-sidebyside"
import { AddOnCategoryIcon } from "./add-on-icons"

interface Props {
  recommendation: Recommendation
  answers: Answers
  prices: PriceMap
  productIds: PriceMap
  insurers: InsurersData
  market: QuizMarket
}

const FREE_ADDONS: ReadonlySet<AddOnId> = new Set(["trend_overlay"])

const TIER_TO_SERVICE: Record<BaseTierId, ServiceId> = {
  blood_only: "blood-test",
  advanced_mri: "advanced-mri",
  pro_mri: "pro-mri",
}

// No CMS biomarker data on the quiz result — the ProductInfoButton degrades
// gracefully (it just skips the biomarker section).
const NO_CMS_BIOMARKERS: BiomarkerGroup[] = []

/** Small circular package icon that sits inline-left of the title.
 *  Sized to fit within the title's line height (~28px) so the title
 *  row doesn't break. Color follows the pricing-page package
 *  coding: green for Core / blood-only, blue for Advanced, pink for
 *  Pro. */
function InlineCircleIcon({ tier }: { tier: BaseTierId }) {
  // Blood-only: real product thumbnail — same blood-cells image the
  // BloodTestAltCard uses in its inline title row when the main rec
  // is MRI-led. Keeps the visual language consistent across both
  // surfaces where this product can appear.
  if (tier === "blood_only") {
    return (
      <Image
        src={blobMediaUrl("/images/addons/advanced-blood-test-card.png")}
        alt=""
        width={28}
        height={28}
        className="h-7 w-7 flex-shrink-0 rounded-full object-cover"
      />
    )
  }
  const bg = tier === "pro_mri" ? "bg-aubergine-300" : "bg-light-blue-300"
  return (
    <span
      className={
        "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full " +
        bg
      }
    >
      <PersonPackageIcon size={18} className="text-black" />
    </span>
  )
}

/**
 * Format a price, or fall back to the caller's "price coming soon" label.
 *
 * A market can legitimately ship NO prices at all: `fetchPrices` returns an
 * empty map for an EUR market that isn't bookable yet (`hasAuthoritativePricing`
 * keys off `*_LAUNCHED`), which is the documented contract so consumers "hide
 * the price line entirely rather than fabricate a figure". `Number(undefined)`
 * is NaN, so every unguarded site rendered "EUR NaN" — on a preview where
 * NL_STATUS wasn't `checkup`, that was EVERY price on the result page.
 */
function priceOrLabel(
  raw: unknown,
  currency: string,
  country: string,
  comingSoonLabel: string
): string {
  const n = Number(raw)
  return Number.isFinite(n)
    ? `${currency} ${formatNumber(n, country)}`
    : comingSoonLabel
}

export function PackageComparison({
  recommendation,
  answers,
  prices,
  productIds,
  insurers,
  market,
}: Props) {
  const { code: country, currency } = useCountry()
  const { openWaitlist } = useWaitlist()
  // The recommended tier may be a product this market hasn't opened yet
  // (`coming_soon` at `checkup` status). When so, the card swaps its booking
  // deep-link for the waitlist modal — same treatment every other surface
  // gives a coming-soon product, and it keeps us from deep-linking into the
  // booking app for something it can't sell.
  const tierBookable = isBookable(market.tiers[recommendation.baseTier])
  // The real locale — used for the booking URL and the list formatter. Do NOT
  // narrow it for those: with the old `en|fr ? locale : "de"` narrowing, a Dutch
  // visitor got a German list conjunction ("... und ...") in the add-ons summary
  // and a `locale=de` booking link. Only the coverage lookup needs the narrowed
  // value, because CoverageRule only carries de/en/fr notes.
  const locale = useLocale()
  const coverageLocale: "de" | "en" | "fr" =
    locale === "en" || locale === "fr" ? locale : "de"
  const t = useTranslations("quiz")
  // Reuses the shared `ui` coming-soon / waitlist labels rather than minting
  // quiz-specific ones — they already exist, translated, in every locale.
  const ut = useTranslations("ui")
  const tMain = useTranslations(`quiz.products.${recommendation.baseTier}`)
  const tAddOns = useTranslations("quiz.addOns")
  const tWhy = useTranslations("quiz.why.eyebrows")
  const tInterests = useTranslations("quiz.why.interests")

  // Local editable copy of the add-on selection — the recommendation
  // is the starting point; the user can remove items from the card.
  // Total and booking URL recompute from this state.
  const [selectedAddOns, setSelectedAddOns] = useState<AddOnId[]>(
    recommendation.addOns
  )

  const removeAddOn = (id: AddOnId) =>
    setSelectedAddOns((prev) => prev.filter((x) => x !== id))

  const total = totalPrice(
    prices,
    recommendation.baseTier,
    selectedAddOns,
    answers.gender
  )

  // Strikethrough + out-of-pocket only when we know the reimbursement
  // amount exactly. "likely" / "unknown" stay headline-priced — a
  // strikethrough on an estimate would over-promise.
  let originalPrice: string | undefined
  let priceNote: string | undefined
  let priceShown = total
  if (
    answers.insurer_id &&
    answers.insurer_id !== "none" &&
    answers.insurer_package_id
  ) {
    const coverage = resolveCoverageState(
      insurers,
      answers.insurer_id,
      answers.insurer_package_id,
      TIER_TO_SERVICE[recommendation.baseTier],
      coverageLocale
    )
    if (coverage.state === "known" && coverage.amount && coverage.amount > 0) {
      originalPrice = formatNumber(total, country)
      priceShown = Math.max(0, total - coverage.amount)
      priceNote = t("result.coverageSavingsNote", {
        amount: String(coverage.amount),
      })
    }
  }

  const baseInfo = baseTierInfo(recommendation.baseTier)
  const baseFeatures = tMain.raw("features") as string[]

  // Base-package "why?" eyebrow — one line that rides with the card.
  // Pro tier with brain interest has its own line so the reason
  // matches the actual tier upgrade.
  const baseWhyKey =
    recommendation.baseTier === "pro_mri" && answers.brain_interest
      ? "proMriBrain"
      : recommendation.baseTier === "pro_mri"
        ? "proMri"
        : recommendation.baseTier === "advanced_mri"
          ? "advancedMri"
          : "bloodOnly"
  const baseWhyReason = tWhy(baseWhyKey as never)

  // Trend overlay sits at the bottom — it's an analysis layer over
  // the selected blood tests, not a peer product, so it reads as a
  // footer rather than a sibling tile.
  const orderedAddOns: AddOnId[] = [
    ...selectedAddOns.filter((id) => id !== "trend_overlay"),
    ...selectedAddOns.filter((id) => id === "trend_overlay"),
  ]

  const addOns: RecommendationAddOn[] = orderedAddOns.map((id) => {
    const info = addOnInfo(id, answers.gender)
    const isFree = FREE_ADDONS.has(id)
    const key = addOnPriceKey(id, answers.gender)
    const priceLabel = isFree
      ? t("result.free")
      : key
        ? priceOrLabel(
            prices[key],
            currency,
            country,
            t("result.priceComingSoon")
          )
        : ""
    // Only markets that actually sell membership may advertise a member price.
    // Without this the fallback label ("member price") rendered on NL, dangling
    // a discount tied to a product the market can't sell yet.
    const memberChf =
      market.membership && !isFree && key ? memberPriceChf(prices, key) : null
    const memberPriceLabel =
      market.membership && !isFree
        ? memberChf !== null
          ? `${currency} ${formatNumber(memberChf, country)}`
          : key
            ? t("result.membership.memberPriceFallback")
            : undefined
        : undefined
    const memberSuffix =
      memberChf !== null ? t("result.membership.memberSuffix") : undefined
    const name = tAddOns(`${id}.name`)
    return {
      id,
      name,
      priceLabel,
      isFree,
      memberPriceLabel,
      memberSuffix,
      infoSlot: info ? (
        <ProductInfoButton data={info} biomarkerGroups={NO_CMS_BIOMARKERS} />
      ) : undefined,
      iconSlot: <AddOnCategoryIcon id={id} alt={name} />,
    }
  })

  // One-sentence summary rendered below the "Add-ons" label.
  // Concatenates the per-add-on interest descriptors with a
  // locale-aware "conjunction" list-formatter ("a, b, and c").
  // Trend overlay is included even though it's free since it's
  // still a deliberate inclusion the user benefits from seeing.
  const interests = orderedAddOns.map((id) => tInterests(id as never))
  const formatter = new Intl.ListFormat(locale, {
    style: "long",
    type: "conjunction",
  })
  const addOnsSummary =
    interests.length > 0
      ? t("why.addOnsSummary", { interests: formatter.format(interests) })
      : undefined

  const ctaHref = withQuizAttribution(
    bookingUrlForCountry(country, locale, [
      baseTierBookingProductId(recommendation.baseTier),
      ...resolveAddOnIds(selectedAddOns, answers.gender, productIds),
    ])
  )

  const controlCard = (
    <div className="mx-auto max-w-[556px]">
      <RecommendationCard
        name={tMain("name")}
        icon={<InlineCircleIcon tier={recommendation.baseTier} />}
        baseInfoSlot={
          <ProductInfoButton
            data={baseInfo}
            biomarkerGroups={NO_CMS_BIOMARKERS}
          />
        }
        badge={t("result.personalizedBadge")}
        whyReason={baseWhyReason}
        description={tMain("description", {
          // blood_only's description carries the live base price; the MRI
          // tiers' descriptions have no {price} arg and ignore the value.
          price: priceOrLabel(
            prices.blood,
            currency,
            country,
            t("result.priceComingSoon")
          ),
        })}
        price={
          // NaN when the market ships no figure for this package (EUR markets
          // omit MRI prices while imaging is coming-soon). Fall through to the
          // coming-soon label rather than rendering "EUR NaN".
          Number.isFinite(priceShown)
            ? formatNumber(priceShown, country)
            : undefined
        }
        priceComingSoonLabel={t("result.priceComingSoon")}
        currency={currency}
        originalPrice={originalPrice}
        priceNote={priceNote}
        priceBreakdownNote={
          Number.isFinite(priceShown) && selectedAddOns.length > 0
            ? t("result.priceWithAddOns")
            : undefined
        }
        features={baseFeatures}
        addOns={addOns}
        addOnsLabel={t("result.addOnsLabel")}
        addOnsSummary={addOnsSummary}
        onRemoveAddOn={removeAddOn}
        ctaLabel={
          tierBookable
            ? t("result.bookPersonalisedCheckup")
            : ut("joinWaitlist")
        }
        ctaHref={ctaHref}
        comingSoonLabel={tierBookable ? undefined : ut("comingSoon")}
        onCtaClick={() =>
          track.ctaClick("quiz_result_book_cta", "quiz-result", ctaHref)
        }
        onWaitlistClick={
          tierBookable
            ? undefined
            : () => {
                track.ctaClick(
                  "quiz_result_waitlist_cta",
                  "quiz-result",
                  "waitlist-modal"
                )
                openWaitlist()
              }
        }
      />
    </div>
  )

  // Blood-only CH results show the membership-vs-one-off side-by-side; every
  // other result (MRI tiers, non-CH) gets the single recommendation card.
  // This shipped as the winner of the `quiz-result-membership` experiment
  // (concluded on `membership-default`); it is no longer flag-gated.
  const membershipEligible = isQuizMembershipEligible(
    recommendation.baseTier,
    country
  )
  if (!membershipEligible) return controlCard

  // Membership headline = recurring fee only (gross 299; the blood test is
  // included in it). No reimbursement netted off here — the "Coverage unclear"
  // band below carries insurance in this variant. Add-ons are one-time and
  // surfaced on a separate line.
  const base = Number(prices.blood)
  const memberAddOnsTotal =
    membershipTotalChf(prices, selectedAddOns, answers.gender) - base
  const standardAddOnsTotal = total - base
  const hasPaidAddOns = selectedAddOns.some((id) => !FREE_ADDONS.has(id))

  return (
    <QuizMembershipSideBySide
      currency={currency}
      membershipPrice={formatNumber(base, country)}
      membershipAddOnsTotal={
        memberAddOnsTotal === 0
          ? undefined
          : formatNumber(memberAddOnsTotal, country)
      }
      oneOffPrice={formatNumber(base, country)}
      oneOffAddOnsTotal={
        standardAddOnsTotal === 0
          ? undefined
          : formatNumber(standardAddOnsTotal, country)
      }
      hasPaidAddOns={hasPaidAddOns}
      oneOffCtaHref={ctaHref}
      addOns={addOns}
      addOnsSummary={addOnsSummary}
      onRemoveAddOn={removeAddOn}
    />
  )
}
